// Template WhatsApp — teks pesan yang sudah di-prefill di SEMUA tombol wa.me
// toko, bisa disunting per-toko dari Akun › Pengaturan › Template WhatsApp.
//
// Menggantikan fitur "WhatsApp Otomatis" (mig 112) yang dicabut: yang dulu
// hendak dikirim MESIN, kini adalah pesan yang dikirim MANUSIA lewat tombol —
// dan itulah yang benar-benar dipakai toko hari ini.
//
// ── KENAPA TEKS BAWAAN TINGGAL DI KODE, BUKAN DI DB ──────────────────────────
// `site_settings` hanya menyimpan OVERRIDE (key JSON `wa_message_templates`).
// Akibatnya: toko yang belum pernah menyimpan langsung memakai teks bawaan,
// tombol yang ditambah nanti otomatis punya bawaan tanpa seed, dan tak ada
// fungsi provisioning yang perlu dipatch tiap kali daftar ini bertambah.
//
// ── KENAPA BAGIAN TERHITUNG JADI SATU VARIABEL ───────────────────────────────
// Rincian nota, rekap varian PO, dan blok stok TIDAK dipecah jadi variabel
// per-baris. Struktur angka adalah milik kode (ia menopang uang), sedangkan
// yang ingin diubah pemilik toko adalah sapaan, framing, dan penutupnya. Jadi
// seluruh blok terhitung masuk sebagai SATU variabel: admin boleh
// memindahkannya dan membungkusnya dengan kata-kata sendiri, tapi tak bisa
// merusak susunannya.
//
// Modul ini MURNI (tanpa Supabase/React) supaya bisa diuji utuh — pasangan
// datanya di `src/lib/supabase/waTemplates.ts`, contoh pratinjau di
// `src/lib/waTemplateSamples.ts`.

/** Key `site_settings` tempat override template toko disimpan (nilai JSON). */
export const WA_TEMPLATES_SETTING_KEY = "wa_message_templates";

/** Kolom pengelompokan di halaman admin. */
export type WaTemplateGroupKey =
  | "admin_ke_pembeli"
  | "pembeli_ke_admin"
  | "reseller_katalog";

export const WA_TEMPLATE_GROUPS: readonly {
  key: WaTemplateGroupKey;
  label: string;
  hint: string;
}[] = [
  {
    key: "admin_ke_pembeli",
    label: "Admin → Pembeli",
    hint: "Tombol di panel admin untuk menghubungi pembeli.",
  },
  {
    key: "pembeli_ke_admin",
    label: "Pembeli → Admin Toko",
    hint: "Tombol yang diklik pembeli di toko untuk menghubungi admin.",
  },
  {
    key: "reseller_katalog",
    label: "Bagikan & Katalog",
    hint: "Tombol bagikan produk, katalog reseller, dan direktori mitra.",
  },
];

/**
 * Satu variabel yang diisi otomatis. `token` dipakai APA ADANYA di dalam
 * template (termasuk kurung sikunya) dan tak bisa diubah admin — lihat
 * `validateWaTemplate`.
 */
export type WaTemplateVar = {
  /** Bentuk yang ditulis di template, mis. `[nama pelanggan]`. */
  token: string;
  /** Penjelasan singkat untuk chip di UI. */
  desc: string;
  /**
   * true = template DITOLAK bila variabel ini hilang. Dipakai untuk muatan
   * yang tanpanya pesan jadi tak berguna (rincian, total, link).
   */
  required?: boolean;
};

export type WaTemplateDef = {
  key: string;
  group: WaTemplateGroupKey;
  /** Nama tombolnya, seperti yang terbaca di layar. */
  label: string;
  /** Di mana tombolnya berada — supaya admin tahu apa yang sedang ia ubah. */
  where: string;
  vars: readonly WaTemplateVar[];
  /** Teks bawaan; harus lolos `validateWaTemplate` terhadap def-nya sendiri. */
  fallback: string;
};

/** Peta nilai variabel: key = token lengkap berikut kurung sikunya. */
export type WaTemplateVars = Readonly<Record<string, string>>;

// ─── Mesin render ────────────────────────────────────────────────────────────

/**
 * Token apa pun yang berbentuk `[...]`. Sengaja tidak mengizinkan `[` bersarang
 * maupun baris baru di dalamnya: yang dicari adalah label pendek, dan pola yang
 * lebih longgar akan menelan tanda kurung siku yang kebetulan dipakai admin.
 */
const TOKEN_RE = /\[[^[\]\n]+\]/g;

/** Rapikan sisa spasi yang ditinggalkan variabel kosong di tengah kalimat. */
function tidyLine(line: string): string {
  return line
    .replace(/ {2,}/g, " ")
    .replace(/ +([,.;:!?])/g, "$1")
    .replace(/ +\*$/, "*")
    .replace(/\s+$/, "");
}

/**
 * Susun pesan akhir dari template + nilai variabel.
 *
 * Tiga aturan yang membuat variabel opsional aman dikosongkan:
 *  1. Baris yang isinya HANYA variabel dan semuanya kosong → baris dibuang,
 *     bukan dicetak sebagai baris menganga.
 *  2. Variabel kosong di TENGAH kalimat → spasi gandanya dirapatkan dan spasi
 *     sebelum tanda baca dibuang. Perapian ini dilewati bila baris yang sama
 *     memuat variabel BLOK (bernilai multi-baris), karena blok terhitung boleh
 *     mengandung perataan spasi yang tak boleh diganggu.
 *  3. Lebih dari satu baris kosong beruntun dirapatkan jadi satu.
 *
 * Token yang TIDAK dikenal dibiarkan apa adanya — `validateWaTemplate` sudah
 * menolaknya sebelum tersimpan, jadi kalau sampai muncul di sini lebih baik ia
 * terlihat daripada raib diam-diam.
 */
export function renderWaTemplate(
  template: string,
  vars: WaTemplateVars
): string {
  const out: string[] = [];

  for (const rawLine of template.split("\n")) {
    if (!TOKEN_RE.test(rawLine)) {
      TOKEN_RE.lastIndex = 0;
      out.push(rawLine.replace(/\s+$/, ""));
      continue;
    }
    TOKEN_RE.lastIndex = 0;

    let sawEmpty = false;
    let sawBlock = false;
    const line = rawLine.replace(TOKEN_RE, (token) => {
      const value = vars[token];
      if (value === undefined) return token;
      if (value === "") {
        sawEmpty = true;
        return "";
      }
      if (value.includes("\n")) sawBlock = true;
      return value;
    });

    if (sawEmpty && rawLine.trim() !== "" && line.trim() === "") continue;
    out.push(sawEmpty && !sawBlock ? tidyLine(line) : line.replace(/\s+$/, ""));
  }

  // Sapaan bawaan "Halo Kak [nama pelanggan]" (uji nyata 2026-10-30, B9) +
  // nama kosong yang pemanggilnya ganti "Kak" tak boleh jadi "Kak Kak".
  return out
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\bKak\s+Kak\b/g, "Kak")
    .trim();
}

// ─── Validasi (inilah yang mengunci variabel) ────────────────────────────────

export type WaTemplateIssues = {
  /** `[...]` yang tidak terdaftar di def — salah ketik / variabel terpotong. */
  unknownTokens: string[];
  /** Variabel wajib yang hilang dari template. */
  missingRequired: string[];
};

/**
 * Gerbang satu-satunya yang membuat variabel "tidak bisa diedit".
 *
 * Editornya textarea biasa — bukan chip contentEditable — karena panel ini
 * mobile-first dan contentEditable + keyboard HP (IME) rewel. Penguncian
 * dipindah ke sini: token asing DAN variabel wajib yang hilang sama-sama
 * menolak Simpan, jadi variabel yang rusak separuh mustahil tersimpan.
 *
 * Efek samping yang DISENGAJA: admin yang mengetik `[penting]` sebagai teks
 * biasa ikut ditolak. Pesan errornya wajib menyebutkan alasan itu.
 */
export function validateWaTemplate(
  def: WaTemplateDef,
  text: string
): WaTemplateIssues {
  const known = new Set(def.vars.map((v) => v.token));
  const found = text.match(TOKEN_RE) ?? [];

  const unknownTokens: string[] = [];
  for (const token of found) {
    if (!known.has(token) && !unknownTokens.includes(token)) {
      unknownTokens.push(token);
    }
  }

  const missingRequired = def.vars
    .filter((v) => v.required && !text.includes(v.token))
    .map((v) => v.token);

  return { unknownTokens, missingRequired };
}

export function waTemplateHasIssue(issues: WaTemplateIssues): boolean {
  return issues.unknownTokens.length > 0 || issues.missingRequired.length > 0;
}

/** Pesan error siap tampil; null bila template sah. */
export function waTemplateIssueMessage(
  def: WaTemplateDef,
  issues: WaTemplateIssues
): string | null {
  if (!waTemplateHasIssue(issues)) return null;
  const parts: string[] = [];
  if (issues.unknownTokens.length) {
    parts.push(
      `Variabel tidak dikenal: ${issues.unknownTokens.join(", ")}. ` +
        "Variabel hanya boleh disisipkan lewat tombol di atas kotak teks — " +
        "teks biasa di dalam kurung siku juga ikut ditolak."
    );
  }
  if (issues.missingRequired.length) {
    parts.push(
      `Variabel wajib hilang: ${issues.missingRequired.join(", ")}. ` +
        "Tanpa itu pesannya kehilangan isi pokoknya."
    );
  }
  parts.push(`Variabel yang tersedia: ${def.vars.map((v) => v.token).join(", ")}.`);
  return parts.join(" ");
}

// ─── Simpan / muat ──────────────────────────────────────────────────────────

export type WaTemplateOverrides = Readonly<Record<string, string>>;

/**
 * Baca nilai key `wa_message_templates`. Nilai rusak (bukan JSON, bukan objek,
 * key tak dikenal, nilai bukan string) DIABAIKAN, bukan melempar: satu baris
 * setelan yang korup tidak boleh mematikan tombol WhatsApp di seluruh toko.
 */
export function parseWaTemplates(
  raw: string | null | undefined
): WaTemplateOverrides {
  if (!raw?.trim()) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

  const out: Record<string, string> = {};
  for (const def of WA_TEMPLATE_DEFS) {
    const value = (parsed as Record<string, unknown>)[def.key];
    if (typeof value === "string") out[def.key] = value;
  }
  return out;
}

/**
 * Serialisasi untuk disimpan. Template yang SAMA PERSIS dengan bawaannya tidak
 * ikut ditulis — supaya perbaikan teks bawaan di kode ke depan tetap sampai ke
 * toko yang tak pernah menyuntingnya.
 */
export function serializeWaTemplates(map: WaTemplateOverrides): string {
  const out: Record<string, string> = {};
  for (const def of WA_TEMPLATE_DEFS) {
    const value = map[def.key];
    if (typeof value !== "string") continue;
    if (value === def.fallback) continue;
    out[def.key] = value;
  }
  return JSON.stringify(out);
}

/** Template yang berlaku untuk satu tombol: override toko, atau bawaan kode. */
export function resolveWaTemplate(
  key: string,
  overrides?: WaTemplateOverrides | null
): string {
  const override = overrides?.[key];
  if (typeof override === "string" && override.trim()) return override;
  return waTemplateDef(key).fallback;
}

export function waTemplateDef(key: string): WaTemplateDef {
  const def = WA_TEMPLATE_DEFS.find((d) => d.key === key);
  if (!def) throw new Error(`Template WhatsApp tidak dikenal: ${key}`);
  return def;
}

export function waTemplatesByGroup(group: WaTemplateGroupKey): WaTemplateDef[] {
  return WA_TEMPLATE_DEFS.filter((d) => d.group === group);
}

// ─── Variabel yang dipakai berulang ─────────────────────────────────────────

const V_NAMA_PELANGGAN: WaTemplateVar = {
  token: "[nama pelanggan]",
  desc: "Nama pembeli",
};
const V_NAMA_TOKO: WaTemplateVar = {
  token: "[nama toko]",
  desc: "Nama toko dari Pengaturan Website",
};
const V_VIA_METODE: WaTemplateVar = {
  token: "[via metode]",
  desc: "Frasa ' via Transfer BCA'; kosong bila metode tak dipilih",
};

// ─── Definisi template ──────────────────────────────────────────────────────

export const WA_TEMPLATE_DEFS: readonly WaTemplateDef[] = [
  // ══ Kolom 1 — Admin → Pembeli ═════════════════════════════════════════════
  {
    key: "invoice_share",
    group: "admin_ke_pembeli",
    label: "Bagikan Tagihan ke WhatsApp",
    where: "Admin › Tagihan › Detail Tagihan → tombol Bagikan Tagihan ke WhatsApp",
    vars: [
      { token: "[nomor nota]", desc: "Nomor tagihan berikut jenisnya, mis. #12 (DP)", required: true },
      V_NAMA_TOKO,
      V_NAMA_PELANGGAN,
      { token: "[rincian tagihan]", desc: "Daftar item (harga umum dicoret), biaya, potongan & rincian DP", required: true },
      { token: "[total tagihan]", desc: "Total tagihan, mis. Rp 250.000", required: true },
      { token: "[baris sudah dibayar]", desc: "Baris 'Sudah dibayar: Rp …'; kosong bila belum ada pembayaran" },
      { token: "[status tagihan]", desc: "'*Status: LUNAS ✅*', '*Sisa tagihan: Rp …*', atau status MENUNGGU ACC untuk pengajuan yang nominalnya belum final", required: true },
      { token: "[blok rekening]", desc: "Rekening tujuan transfer (bank/e-wallet); kosong bila tagihan tak bisa dibayar lagi" },
      { token: "[baris catatan]", desc: "Baris 'Catatan: …' dari catatan tagihan; kosong bila tak ada" },
      { token: "[baris peringatan tempo]", desc: "Peringatan batas waktu bayar; kosong bila tagihan tak bertempo" },
      { token: "[link nota]", desc: "Tautan halaman tagihan pembeli (perlu login untuk membayar)", required: true },
      { token: "[blok unduh pdf]", desc: "Ajakan + tautan unduh tagihan PDF tanpa login; kosong bila tautannya tak ada" },
    ],
    fallback: [
      "*TAGIHAN [nomor nota]*",
      "[nama toko]",
      "",
      "Halo Kak [nama pelanggan], berikut rincian tagihanmu:",
      "",
      "[rincian tagihan]",
      "",
      "━━━━━━━━━━━━━━",
      "*Total: [total tagihan]*",
      "[baris sudah dibayar]",
      "[status tagihan]",
      "",
      "[blok rekening]",
      "",
      "[baris catatan]",
      "",
      "[baris peringatan tempo]",
      "",
      "Lihat & bayar tagihan di sini:",
      "[link nota]",
      "",
      "[blok unduh pdf]",
      "",
      "Terima kasih 🙏",
    ].join("\n"),
  },
  {
    key: "invoice_reminder",
    group: "admin_ke_pembeli",
    label: "Tagih via WhatsApp (banyak tagihan)",
    where: "Admin › Reseller › Tagihan → tab Terbuka → tombol Tagih via WhatsApp",
    vars: [
      { token: "[jumlah nota]", desc: "Frasa ' (3 TAGIHAN)'; kosong bila cuma satu tagihan" },
      V_NAMA_TOKO,
      V_NAMA_PELANGGAN,
      { token: "[kalimat pembuka]", desc: "'Ada 3 tagihan yang masih belum lunas. Berikut rinciannya:'", required: true },
      { token: "[rincian nota]", desc: "Blok rincian tiap tagihan berikut tautannya", required: true },
      { token: "[total harus dibayar]", desc: "Jumlah seluruh sisa tagihan", required: true },
      { token: "[ringkasan jumlah]", desc: "'Dari 3 tagihan · nilai Rp …'" },
      { token: "[baris sudah dibayar]", desc: "Baris 'Sudah dibayar: Rp …'; kosong bila belum ada pembayaran" },
      { token: "[blok bayar sekaligus]", desc: "Ajakan + tautan bayar semua sekaligus; kosong bila tautannya tak ada" },
      { token: "[blok daftar tagihan]", desc: "Ajakan + tautan daftar semua tagihan; kosong bila tak ada" },
      { token: "[catatan admin]", desc: "Catatan penutup yang diketik admin sendiri; kosong bila dikosongkan" },
    ],
    fallback: [
      "*TAGIHAN BELUM LUNAS[jumlah nota]*",
      "[nama toko]",
      "",
      "Halo Kak [nama pelanggan] 👋",
      "[kalimat pembuka]",
      "",
      "[rincian nota]",
      "",
      "━━━━━━━━━━━━━━",
      "*TOTAL YANG HARUS DIBAYAR: [total harus dibayar]*",
      "[ringkasan jumlah]",
      "[baris sudah dibayar]",
      "",
      "[blok bayar sekaligus]",
      "",
      "[blok daftar tagihan]",
      "",
      "[catatan admin]",
      "",
      "Mohon segera diselesaikan ya 🙏",
      "Terima kasih!",
    ].join("\n"),
  },
  {
    // Kembaran RINGKAS `invoice_reminder` bila SEMUA nota yang ditagih adalah
    // nota DP (pemilik 2026-10-05): nota DP cuma berisi satu baris "DP —
    // <produk>", jadi blok Terbit/Total nota/Lihat nota per nota hanya
    // memanjangkan pesan. Tiap nota = satu baris "Tagihan DP — produk — Rp",
    // rinciannya dirujuk ke /tagihan yang tersaring DP. Sengaja TANPA tautan
    // "Bayar semua sekaligus" (pemilik 2026-10-05) — pembeli membayar dari
    // daftar Tagihan DP itu.
    key: "invoice_reminder_dp",
    group: "admin_ke_pembeli",
    label: "Tagih via WhatsApp (khusus DP)",
    where:
      "Admin › Reseller › Tagihan → tab Terbuka → tombol Tagih via WhatsApp, bila semua tagihan terpilih adalah Tagihan DP",
    vars: [
      { token: "[jumlah nota]", desc: "Frasa ' (3 TAGIHAN)'; kosong bila cuma satu tagihan" },
      V_NAMA_TOKO,
      V_NAMA_PELANGGAN,
      { token: "[kalimat pembuka]", desc: "'Ada 3 tagihan yang masih belum lunas. Berikut rinciannya:'", required: true },
      { token: "[rincian dp]", desc: "Satu baris per tagihan: 'Tagihan DP — nama produk — Rp sisa'", required: true },
      { token: "[total harus dibayar]", desc: "Jumlah seluruh sisa DP", required: true },
      { token: "[blok rincian dp]", desc: "Ajakan + tautan daftar Tagihan DP pembeli; kosong bila tak ada" },
      { token: "[catatan admin]", desc: "Catatan penutup yang diketik admin sendiri; kosong bila dikosongkan" },
    ],
    fallback: [
      "*TAGIHAN BELUM LUNAS[jumlah nota]*",
      "[nama toko]",
      "",
      "Halo Kak [nama pelanggan] 👋",
      "[kalimat pembuka]",
      "",
      "[rincian dp]",
      "",
      "━━━━━━━━━━━━━━",
      "*Total DP yang harus dibayarkan: [total harus dibayar]*",
      "",
      "[blok rincian dp]",
      "",
      "[catatan admin]",
      "",
      "Mohon segera diselesaikan ya 🙏",
      "Terima kasih!",
    ].join("\n"),
  },
  {
    key: "po_confirm",
    group: "admin_ke_pembeli",
    label: "Konfirmasi pesanan PO",
    where: "Admin › Detail PO (Open & Closed PO) → tab Rekap Per Pelanggan → tombol Konfirmasi",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[nama produk]", desc: "Nama produk berikut mereknya", required: true },
      { token: "[estimasi ready]", desc: "Perkiraan barang siap", required: true },
      {
        token: "[rincian varian]",
        desc: "Daftar varian yang dipesan berikut jumlahnya — plus harga jual umum, diskon & subtotal tiap varian bila harganya sudah diatur",
        required: true,
      },
      { token: "[total pcs]", desc: "Total pcs yang dipesan", required: true },
      {
        token: "[baris total harga]",
        desc: "Baris 'Total Harga = Rp …'; kosong bila harga belum diatur. Hapus variabel ini untuk mengirim pesan TANPA harga",
      },
      { token: "[blok cek pesanan]", desc: "Ajakan + tautan ke keranjang pembeli; kosong bila tautannya tak ada" },
    ],
    fallback: [
      "*Kak [nama pelanggan]*",
      "Mau konfirmasi dulu ya sebelum pesanan PO kami proses",
      "Berikut Rekap Pesanan PO kaka :",
      "",
      "*[nama produk]*",
      "Estimasi : [estimasi ready]",
      "",
      "[rincian varian]",
      "",
      // Tanpa spasi sebelum `*` penutup: "*… 4 *" tak ditebalkan WhatsApp.
      "*Total Produk = [total pcs] pcs*",
      "[baris total harga]",
      "",
      "[blok cek pesanan]",
      "",
      "Silakan dicek datanya ya, Kak.",
      'Kalau sudah benar, balas pesan ini dengan "KONFIRMASI" supaya pesanan langsung kami proses ke tahap Pre Order.',
    ].join("\n"),
  },
  {
    // Kembaran `po_confirm` untuk FASE PRODUKSI (pemilik 2026-09-29): angka &
    // rinciannya dibuat pembuat yang sama, tapi kalimatnya rekap — pesanan
    // sudah diproduksi (sebagian bahkan sudah dikirim), jadi ajakan "balas
    // KONFIRMASI supaya kami proses ke tahap Pre Order" milik po_confirm salah
    // tahap di sini.
    key: "production_recap",
    group: "admin_ke_pembeli",
    label: "Rekap pesanan produksi",
    where: "Admin › Detail Produksi → kolom Produksi → tombol Rekap kartu pelanggan",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[nama produk]", desc: "Nama produk berikut mereknya", required: true },
      { token: "[estimasi ready]", desc: "Perkiraan barang siap", required: true },
      {
        token: "[rincian varian]",
        desc: "Daftar varian pesanan aktif (termasuk yang sudah ready/dikirim) berikut jumlahnya — plus harga jual umum, diskon & subtotal tiap varian bila harganya sudah diatur",
        required: true,
      },
      { token: "[total pcs]", desc: "Total pcs pesanan aktif (tanpa yang gagal produksi)", required: true },
      {
        token: "[baris total harga]",
        desc: "Baris 'Total Harga = Rp …'; kosong bila harga belum diatur. Hapus variabel ini untuk mengirim pesan TANPA harga",
      },
      {
        token: "[baris gagal produksi]",
        desc: "Baris 'Gagal produksi: N pcs (dibatalkan, tidak dihitung)'; kosong bila tak ada yang gagal",
      },
      { token: "[blok cek pesanan]", desc: "Ajakan + tautan ke keranjang pembeli; kosong bila tautannya tak ada" },
    ],
    fallback: [
      "*Kak [nama pelanggan]*",
      "Berikut rekap pesanan PO kaka yang sedang kami produksi :",
      "",
      "*[nama produk]*",
      "Estimasi : [estimasi ready]",
      "",
      "[rincian varian]",
      "",
      "*Total Produk = [total pcs] pcs*",
      "[baris total harga]",
      "[baris gagal produksi]",
      "",
      "[blok cek pesanan]",
      "",
      "Terima kasih, Kak 🙏",
    ].join("\n"),
  },
  {
    key: "production_ready",
    group: "admin_ke_pembeli",
    label: "Kabar barang sudah READY",
    where: "Admin › Detail Produksi → kartu reseller → tautan WhatsApp",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[nama produk]", desc: "Nama produk berikut mereknya", required: true },
      { token: "[rincian varian]", desc: "Daftar varian yang sudah ready berikut jumlahnya", required: true },
      { token: "[total ready]", desc: "Total pcs yang sudah ready", required: true },
      { token: "[blok atur pengiriman]", desc: "Ajakan + tautan atur pengiriman; kosong bila tautannya tak ada" },
    ],
    fallback: [
      "*Kak [nama pelanggan]*",
      "Kabar baik! Pesanan Pre Order kaka sudah READY 🎉",
      "Berikut rincian barang yang sudah siap :",
      "",
      "*[nama produk]*",
      "",
      "[rincian varian]",
      "",
      "*Total Ready = [total ready] pcs *",
      "",
      "[blok atur pengiriman]",
      "",
      "Silakan buka menu Pesanan Saya > Sudah Ready, pilih barangnya, lalu ajukan pengiriman ya, Kak.",
      "Kalau ada yang mau ditanyakan, balas pesan ini saja.",
    ].join("\n"),
  },
  {
    key: "cart_recap",
    group: "admin_ke_pembeli",
    label: "Rekap Barang keranjang",
    where: "Admin › Keranjang pelanggan → tombol Rekap ke WhatsApp",
    vars: [
      { token: "[jenis keranjang]", desc: "PRE ORDER / READY / dst", required: true },
      { token: "[tanggal]", desc: "Tanggal rekap dibuat" },
      V_NAMA_PELANGGAN,
      { token: "[rincian barang]", desc: "Daftar barang terpisah #Belum Lunas & #Sudah Lunas", required: true },
    ],
    fallback: [
      "*REKAP PRODUK [jenis keranjang]*",
      "[tanggal]",
      "*[nama pelanggan]*",
      "",
      "[rincian barang]",
    ].join("\n"),
  },
  {
    key: "delivery_note",
    group: "admin_ke_pembeli",
    label: "Kirim Surat Jalan (PDF)",
    where: "Admin › Detail Pengiriman → Kirim Surat Jalan (PDF) ke WhatsApp",
    vars: [
      { token: "[nama penerima]", desc: "Nama penerima paket" },
      { token: "[nomor pengiriman]", desc: "Kode pengiriman tanpa tanda pagar", required: true },
      V_NAMA_TOKO,
      { token: "[rincian paket]", desc: "Isi paket, berat, ekspedisi, resi, sisa tagihan", required: true },
      { token: "[blok berkas pdf]", desc: "Ajakan + tautan berkas PDF; kosong bila PDF dikirim sebagai lampiran" },
    ],
    fallback: [
      "Halo Kak [nama penerima] 👋",
      "Berikut *Surat Jalan* pesanan kamu (#[nomor pengiriman]) dari [nama toko].",
      "",
      "[rincian paket]",
      "",
      "[blok berkas pdf]",
      "",
      "Terima kasih 🙏",
    ].join("\n"),
  },
  {
    key: "invoice_pdf_share",
    group: "admin_ke_pembeli",
    label: "Kirim Detail Tagihan (PDF)",
    where: "Admin › Detail Tagihan → kartu Dokumen tagihan → Kirim ke WhatsApp",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[judul nota]", desc: "Judul tagihan, mis. Tagihan #12", required: true },
      V_NAMA_TOKO,
      { token: "[rincian tagihan]", desc: "Total, sudah dibayar, sisa/status, tanggal lunas", required: true },
      { token: "[baris peringatan tempo]", desc: "Peringatan batas waktu bayar; kosong bila tagihan tak bertempo" },
      { token: "[baris bayar]", desc: "Tautan bayar atau tautan detail tagihan" },
    ],
    fallback: [
      "Halo Kak [nama pelanggan] 👋",
      "Berikut *[judul nota]* dari [nama toko].",
      "",
      "[rincian tagihan]",
      "",
      "[baris peringatan tempo]",
      "",
      "[baris bayar]",
      "",
      "Rincian lengkapnya ada di PDF terlampir. Terima kasih 🙏",
    ].join("\n"),
  },
  {
    key: "password_reset_done",
    group: "admin_ke_pembeli",
    label: "Kirim Password via WhatsApp",
    where: "Admin › Pelanggan › Edit → tombol Kirim Password via WhatsApp",
    vars: [
      V_NAMA_PELANGGAN,
      V_NAMA_TOKO,
      { token: "[password baru]", desc: "Password hasil reset admin", required: true },
      { token: "[blok link login]", desc: "Baris 'Masuk di: …'; kosong bila tautannya tak ada" },
    ],
    fallback: [
      "Halo Kak [nama pelanggan] 👋",
      "",
      "Password akun [nama toko] kamu sudah direset oleh admin.",
      "",
      "Password baru: [password baru]",
      "",
      "[blok link login]",
      "",
      "Demi keamanan, SEGERA ganti password ini lewat menu Akun Saya › Edit Profil › Ganti Password.",
    ].join("\n"),
  },
  {
    key: "pesanan_dikirim",
    group: "admin_ke_pembeli",
    label: "Kabar pesanan sedang disiapkan",
    where: "Admin › Keranjang → aksi Request Kirim → layar sukses → tautan WhatsApp",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[jumlah pcs]", desc: "Jumlah pcs yang akan dikirim", required: true },
    ],
    fallback:
      "Halo Kak [nama pelanggan], pesanan kamu sedang kami siapkan untuk dikirim ([jumlah pcs] pcs). " +
      "Nomor resinya akan kami kabari setelah paket diserahkan ke kurir.",
  },
  {
    // Uji nyata S01 T10: pesan Request Kirim menjanjikan resi "akan kami
    // kabari", tapi sesudah resi diisi tak ada tombol untuk mengabarinya.
    key: "resi_dikirim",
    group: "admin_ke_pembeli",
    label: "Kabar resi paket",
    where: "Admin › Detail Pengiriman (Sedang Dikirim) → Kabari Resi ke WhatsApp",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[ekspedisi]", desc: "Ekspedisi paket", required: true },
      { token: "[nomor resi]", desc: "Nomor resi dari kurir", required: true },
    ],
    fallback: [
      "Halo Kak [nama pelanggan], paket kamu sudah dikirim ya 📦",
      "Ekspedisi: [ekspedisi]",
      "No. resi: [nomor resi]",
      "",
      "Terima kasih 🙏",
    ].join("\n"),
  },
  // ══ Kolom 2 — Pembeli → Admin Toko ════════════════════════════════════════
  {
    key: "bayar_konfirmasi",
    group: "pembeli_ke_admin",
    label: "Konfirmasi sudah transfer",
    where: "Toko › Bayar Tagihan → tombol Hubungi Admin via WhatsApp",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[nominal]", desc: "Nominal yang ditransfer", required: true },
      { token: "[nomor nota]", desc: "Nomor tagihan yang dibayar", required: true },
      V_VIA_METODE,
    ],
    fallback:
      "Halo Admin, saya [nama pelanggan] sudah transfer [nominal] " +
      "untuk Tagihan [nomor nota][via metode]. Mohon dicek ya 🙏",
  },
  {
    key: "nota_tanya",
    group: "pembeli_ke_admin",
    label: "Kirim Link Tagihan ke Admin",
    where: "Toko › Detail Tagihan → tombol Kirim Link ke Admin",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[judul nota]", desc: "Judul tagihan yang ditanyakan", required: true },
      { token: "[sisa tagihan]", desc: "Frasa ' Sisa tagihan Rp ….'; kosong bila sudah lunas" },
      { token: "[link nota]", desc: "Tautan halaman tagihan", required: true },
    ],
    fallback: [
      "Halo Admin, saya [nama pelanggan] ingin menanyakan [judul nota].[sisa tagihan]",
      "Link tagihan: [link nota]",
      "Mohon dibantu ya 🙏",
    ].join("\n"),
  },
  {
    key: "topup",
    group: "pembeli_ke_admin",
    label: "Pengajuan top-up saldo",
    where: "Toko › Saldo › Top-up → tombol WhatsApp setelah pengajuan",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[nominal]", desc: "Nominal top-up yang diajukan", required: true },
      V_VIA_METODE,
      { token: "[baris verifikasi]", desc: "Tautan verifikasi untuk admin; kosong bila tak ada" },
    ],
    fallback: [
      "Halo Admin, saya [nama pelanggan] mengajukan top-up saldo [nominal][via metode]. Mohon dicek ya 🙏",
      "[baris verifikasi]",
    ].join("\n"),
  },
  {
    key: "mitra_daftar",
    group: "pembeli_ke_admin",
    label: "Pendaftaran Mitra (berbayar)",
    where: "Toko › Mitra → tombol WhatsApp setelah mengajukan pendaftaran",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[nama level]", desc: "Level mitra yang didaftar", required: true },
      { token: "[nominal]", desc: "Nominal top-up pendaftaran", required: true },
      V_VIA_METODE,
      { token: "[baris verifikasi]", desc: "Tautan verifikasi untuk admin; kosong bila tak ada" },
    ],
    fallback: [
      "Halo Admin, saya [nama pelanggan] mendaftar Mitra level [nama level] dengan top-up [nominal][via metode]. Mohon diverifikasi ya 🙏",
      "[baris verifikasi]",
    ].join("\n"),
  },
  {
    key: "mitra_sudah_daftar",
    group: "pembeli_ke_admin",
    label: "Menanyakan pendaftaran Mitra yang sedang diproses",
    where: "Toko › Mitra → kartu pengajuan menunggu → Hubungi Admin via WhatsApp",
    vars: [
      V_NAMA_PELANGGAN,
      { token: "[nama level]", desc: "Level mitra yang didaftar", required: true },
    ],
    fallback:
      "Halo Admin, saya [nama pelanggan] sudah mengajukan pendaftaran Mitra " +
      "level [nama level]. Mohon dicek ya 🙏",
  },
  {
    key: "mitra_tanya",
    group: "pembeli_ke_admin",
    label: "Bertanya soal pendaftaran mitra",
    where: "Toko › Mitra → saat tak ada level yang bisa didaftar → tombol WhatsApp",
    vars: [V_NAMA_PELANGGAN],
    fallback:
      "Halo Admin, saya [nama pelanggan] ingin bertanya soal pendaftaran mitra/reseller.",
  },
  {
    key: "ganti_alamat",
    group: "pembeli_ke_admin",
    label: "Minta ganti alamat pengiriman",
    where: "Toko › Detail Pengiriman → tombol Ganti alamat",
    vars: [
      V_NAMA_TOKO,
      { token: "[nomor pengiriman]", desc: "Kode pengiriman yang terlihat pembeli", required: true },
      { token: "[baris ekspedisi]", desc: "Baris 'Ekspedisi: …'; kosong bila belum dipilih" },
      { token: "[blok alamat sekarang]", desc: "Alamat yang sedang berlaku; kosong bila belum ada" },
    ],
    fallback: [
      "Halo admin [nama toko], saya mau mengubah alamat pengiriman saya.",
      "",
      "Nomor Pengiriman: [nomor pengiriman]",
      "[baris ekspedisi]",
      "",
      "[blok alamat sekarang]",
      "",
      "Alamat baru:",
      "(tulis nama penerima, no. HP, alamat lengkap, kecamatan, kota, kode pos)",
      "",
      "Saya mengerti ongkirnya bisa berubah mengikuti alamat baru — kalau lebih " +
        "mahal saya bersedia membayar selisihnya, kalau lebih murah tolong " +
        "dikembalikan ke saldo.",
      "Terima kasih 🙏",
    ].join("\n"),
  },
  {
    key: "tanya_resi",
    group: "pembeli_ke_admin",
    label: "Tanya nomor resi",
    where: "Toko › Detail Pengiriman → tombol Tanya Nomor Resi ke Admin",
    vars: [
      V_NAMA_TOKO,
      { token: "[nomor pengiriman]", desc: "Kode pengiriman yang terlihat pembeli", required: true },
      { token: "[baris ekspedisi]", desc: "Baris 'Ekspedisi: …'; kosong bila belum dipilih" },
      { token: "[baris tanggal kirim]", desc: "Baris 'Tanggal kirim: …'; kosong bila belum dikirim" },
      { token: "[baris isi paket]", desc: "Baris 'Isi paket: N pcs'; kosong bila tak diketahui" },
      { token: "[kalimat status paket]", desc: "Kalimat berbeda untuk paket yang sudah vs belum dikirim", required: true },
    ],
    fallback: [
      "Halo admin [nama toko], saya mau menanyakan nomor resi pengiriman saya.",
      "",
      "Nomor Pengiriman: [nomor pengiriman]",
      "[baris ekspedisi]",
      "[baris tanggal kirim]",
      "[baris isi paket]",
      "",
      "[kalimat status paket]",
      "Terima kasih 🙏",
    ].join("\n"),
  },
  {
    key: "lupa_password",
    group: "pembeli_ke_admin",
    label: "Minta reset password",
    where: "Toko › Lupa Password → tombol Buka WhatsApp Admin",
    vars: [
      V_NAMA_TOKO,
      { token: "[identitas akun]", desc: "Nomor HP / email yang diketik pembeli", required: true },
    ],
    fallback: [
      "Halo Admin [nama toko] 👋",
      "",
      "Saya lupa password akun saya dan ingin minta bantuan reset.",
      "",
      "Akun saya: [identitas akun]",
      "",
      "Mohon dibantu ya, terima kasih 🙏",
    ].join("\n"),
  },
  {
    key: "minta_aktivasi",
    group: "pembeli_ke_admin",
    label: "Minta aktivasi akun baru",
    where: "Toko › layar Menunggu Persetujuan → tombol WhatsApp",
    vars: [
      V_NAMA_TOKO,
      { token: "[perkenalan]", desc: "'saya Budi dari Toko Budi' — menyusut jadi 'saya' bila datanya belum ada", required: true },
    ],
    fallback:
      "Halo admin [nama toko], [perkenalan] baru saja mendaftar. Mohon aktivasi akun saya.",
  },
  {
    key: "bantuan_belanja",
    group: "pembeli_ke_admin",
    label: "Minta bantuan cara belanja",
    where: "Toko › Tutorial & Panduan → tombol WhatsApp di kaki halaman",
    vars: [V_NAMA_TOKO],
    fallback: "Halo Admin, saya butuh bantuan soal cara belanja di [nama toko]",
  },
  {
    key: "hubungi_admin",
    group: "pembeli_ke_admin",
    label: "Hubungi Admin (umum)",
    where: "Toko › Akun Saya dan halaman Dropship → tombol Hubungi Admin / Tanya Admin via WA",
    vars: [V_NAMA_TOKO],
    fallback: "Halo Admin [nama toko], saya butuh bantuan.",
  },
  {
    key: "cari_mitra_umum",
    group: "pembeli_ke_admin",
    label: "Belum menemukan mitra (toko belum punya mitra)",
    where: "Toko › Cari Mitra → tombol Chat WhatsApp Toko saat daftar mitra kosong",
    vars: [V_NAMA_TOKO],
    fallback:
      "Halo [nama toko], saya mau order tapi belum menemukan mitra di kota saya.",
  },
  {
    key: "cari_mitra_wilayah",
    group: "pembeli_ke_admin",
    label: "Belum menemukan mitra di wilayah ini",
    where: "Toko › Cari Mitra → tombol Chat WhatsApp Toko saat tak ada mitra sekitar",
    vars: [
      V_NAMA_TOKO,
      { token: "[wilayah]", desc: "Kecamatan/kota/provinsi pembeli", required: true },
    ],
    fallback:
      "Halo [nama toko], saya di [wilayah] dan belum menemukan mitra di sekitar sini.",
  },
  {
    key: "order_produk",
    group: "pembeli_ke_admin",
    label: "Order satu produk via WhatsApp",
    where:
      "Toko › Halaman produk → tombol Order via WhatsApp (tamu), dan katalog reseller /toko/[kode]",
    vars: [
      { token: "[tujuan]", desc: "Nama toko atau nama reseller pemilik katalog", required: true },
      { token: "[nama produk]", desc: "Nama produk yang dipesan", required: true },
      { token: "[baris varian]", desc: "Baris 'Varian: …'; kosong bila produk tanpa varian" },
      { token: "[jumlah]", desc: "Jumlah pcs yang dipesan", required: true },
      { token: "[baris harga]", desc: "Baris 'Harga: …'; kosong bila harganya menyusul" },
      { token: "[link produk]", desc: "Tautan halaman produk", required: true },
    ],
    fallback: [
      "Halo [tujuan], saya mau order:",
      "",
      "*[nama produk]*",
      "[baris varian]",
      "Jumlah: [jumlah]",
      "[baris harga]",
      "",
      "[link produk]",
    ].join("\n"),
  },
  // ══ Kolom 3 — Bagikan & Katalog ═══════════════════════════════════════════
  {
    key: "bagikan_produk",
    group: "reseller_katalog",
    label: "Bagikan produk ke WhatsApp",
    where: "Toko › Halaman produk → tombol Bagikan ke WA / Bagikan Katalog Saya",
    vars: [
      { token: "[nama produk]", desc: "Nama produk", required: true },
      V_NAMA_TOKO,
      { token: "[baris harga]", desc: "Baris harga berikut ikonnya; kosong bila harga menyusul" },
      { token: "[baris jenis]", desc: "Baris Pre-Order / Ready Stock" },
      { token: "[blok stok]", desc: "Potret stok per varian; kosong bila tak ditampilkan" },
      { token: "[link produk]", desc: "Tautan halaman produk", required: true },
    ],
    fallback: [
      "*[nama produk]*",
      "[nama toko]",
      "",
      "[baris harga]",
      "[baris jenis]",
      "",
      "[blok stok]",
      "",
      "Lihat & pesan produknya di sini:",
      "[link produk]",
    ].join("\n"),
  },
  {
    key: "bagikan_katalog",
    group: "reseller_katalog",
    label: "Bagikan katalog reseller",
    where: "Toko › Akun Saya › kartu Website Toko Saya → tombol Bagikan",
    vars: [
      { token: "[nama katalog]", desc: "Nama katalog reseller", required: true },
      { token: "[link katalog]", desc: "Tautan katalog", required: true },
    ],
    fallback: [
      "*[nama katalog]*",
      "",
      "Lihat katalog & order produknya di sini:",
      "[link katalog]",
    ].join("\n"),
  },
  {
    key: "broadcast_open_po",
    group: "reseller_katalog",
    label: "Broadcast Open Pre-Order",
    where: "Admin › Form produk → tombol Bagikan ke WhatsApp",
    vars: [
      { token: "[nama produk]", desc: "Nama produk berikut mereknya", required: true },
      { token: "[baris tutup po]", desc: "Baris batas tutup PO; kosong bila tak diisi" },
      { token: "[baris ready]", desc: "Baris estimasi ready; kosong bila tak diisi" },
      { token: "[baris diskon]", desc: "Baris diskon per grup; kosong bila tak ada diskon" },
      { token: "[link produk]", desc: "Tautan halaman produk", required: true },
    ],
    fallback: [
      "*Open Pre-Order*",
      "*[nama produk]*",
      "[baris tutup po]",
      "[baris ready]",
      "[baris diskon]",
      "📝List Web",
      "Link Produk",
      "",
      "[link produk]",
    ].join("\n"),
  },
  {
    key: "katalog_keranjang",
    group: "reseller_katalog",
    label: "Kirim pesanan keranjang katalog",
    where: "Toko › Katalog reseller /toko/[kode] → keranjang → tombol kirim pesanan",
    vars: [
      V_NAMA_TOKO,
      { token: "[rincian item]", desc: "Daftar produk, jumlah, dan hitungan harganya", required: true },
      { token: "[baris total]", desc: "Baris total berikut ongkir", required: true },
      { token: "[baris harga menyusul]", desc: "Catatan produk yang harganya belum masuk hitungan" },
      { token: "[blok alamat]", desc: "Alamat tujuan kirim; kosong bila belum diisi" },
    ],
    fallback: [
      "*Format Order [nama toko]*",
      "",
      "[rincian item]",
      "",
      "[baris total]",
      "[baris harga menyusul]",
      "",
      "[blok alamat]",
    ].join("\n"),
  },
  {
    key: "mitra_direktori",
    group: "reseller_katalog",
    label: "Chat mitra dari direktori",
    where: "Toko › Cari Mitra → kartu mitra → tombol WhatsApp",
    vars: [
      { token: "[nama mitra]", desc: "Nama toko mitra", required: true },
      { token: "[baris asal daftar]", desc: "'di daftar mitra terdekat Galea.'", required: true },
      { token: "[baris wilayah]", desc: "Baris 'Saya di …'; kosong bila wilayah tak diketahui" },
      { token: "[blok produk]", desc: "Produk yang ditanyakan berikut tautannya; kosong bila datang bukan dari halaman produk" },
    ],
    fallback: [
      "Halo [nama mitra], saya menemukan toko ini",
      "[baris asal daftar]",
      "[baris wilayah]",
      "",
      "[blok produk]",
      "",
      "Apakah masih tersedia?",
    ].join("\n"),
  },
  {
    key: "rekap_po",
    group: "reseller_katalog",
    label: "Rekap PO ke WhatsApp",
    where: "Admin › Detail PO → tombol Rekap ke WhatsApp",
    vars: [
      { token: "[nama produk]", desc: "Nama produk", required: true },
      { token: "[baris merek]", desc: "Baris merek; kosong bila produk tanpa merek" },
      { token: "[baris status]", desc: "Status PO berikut tanggal tutupnya", required: true },
      { token: "[estimasi ready]", desc: "Perkiraan barang siap", required: true },
      { token: "[diskon]", desc: "Ringkasan diskon per grup reseller", required: true },
      { token: "[rincian varian]", desc: "Daftar varian yang dipesan berikut jumlahnya", required: true },
      { token: "[total pcs]", desc: "Total pcs dipesan", required: true },
    ],
    fallback: [
      "*[nama produk]*",
      "[baris merek]",
      "[baris status]",
      "▶️Ready Toko : [estimasi ready]",
      "📌Diskon : [diskon]",
      "",
      "[rincian varian]",
      "",
      "Total : *[total pcs] pcs*",
    ].join("\n"),
  },
  {
    key: "rekap_produksi",
    group: "reseller_katalog",
    label: "Rekap Produksi ke WhatsApp",
    where: "Admin › Detail Produksi → tombol Rekap ke WhatsApp",
    vars: [
      { token: "[nama produk]", desc: "Nama produk", required: true },
      { token: "[baris merek]", desc: "Baris merek; kosong bila produk tanpa merek" },
      { token: "[baris status]", desc: "Status produksi", required: true },
      { token: "[ringkasan progres]", desc: "Baris Tiba / Ready / Dikirim / Gagal Produksi" },
      { token: "[rincian varian]", desc: "Daftar varian berikut jumlah & statusnya", required: true },
      { token: "[total pcs]", desc: "Total pcs aktif", required: true },
    ],
    fallback: [
      "*[nama produk]*",
      "[baris merek]",
      "[baris status]",
      "[ringkasan progres]",
      "",
      "[rincian varian]",
      "",
      "Total : *[total pcs] pcs*",
    ].join("\n"),
  },
];
